<template>
  <FieldView class="form-datepicker is-relative">
    <ValidationProvider
      ref="validationProvider"
      :rules="rules"
      v-slot="validationProps"
      :name="formattedLabel"
      :value="computedValue"
    >
      <float-label :on="showFloatLabel" :label="placeholder">
        <DatepickerView
          v-model="computedValue"
          :placeholder="placeholder"
          icon-pack="cls"
          :icon="icon"
          :min-date="minDate"
          :max-date="maxDate"
          :disabled="readOnly"
          :inline="isInline"
          :loading="loading"
          :range="range"
          :position="position"
          :years-range="yearsRange"
          :locale="locale"
          :date-formatter="displayDateFormatter && dateFormatter"
          :mobile-native="isMobileNative"
        >
          <template v-if="$slots.trigger" #trigger="props">
            <slot name="trigger" v-bind="props" />
          </template>
        </DatepickerView>
        <slot name="error" v-bind="validationProps">
          <span class="has-text-danger is-size-7 m-t-3 is-absolute">
            {{ getErrorFormattedText(validationProps) }}
          </span>
        </slot>
      </float-label>
      <slot
        v-if="!hasValidationErrors(validationProps) && (subLabel || $slots.subLabel || hasTooltip)"
        name="subLabel"
        :has-errors="hasMultiSelectValidationErrors(validationProps)"
      >
        <FormSubLabelTooltip
          v-if="hasTooltip"
          :right-label="rightSubLabel"
          :tooltip-label="tooltipLabel"
          :left-label="subLabel"
        />
        <FormSubLabel v-else :label="subLabel" :hide-icon="hideSubLabelIcon" />
      </slot>
    </ValidationProvider>
  </FieldView>
</template>

<script>
import dayjs from 'dayjs';
import FloatLabel from '/@/modules/core/form/components/FloatLabel.vue';
import ValidationProvider from '/@/modules/core/form/components/ValidationProvider.vue';
import { FormInputMixin } from '/@/modules/core/form/mixins/FormInputMixin';
import { fromHyphenISOtoForwardSlashISO, isString } from '/@/modules/core/app/helpers/StringHelper';
import { DateFormat } from '/@/modules/core/app/constants/date.constants';
import Lang from '/@/modules/core/app/services/Lang';
import { FormValidationMixin } from '/@/modules/core/form/mixins/FormValidationMixin';
import { HasCurrentEstateMixin } from '/@/modules/core/app/mixins/HasCurrentEstateMixin';
import { Locale } from '/@/modules/core/app/constants/Locale';
import FormSubLabelTooltip from '/@/modules/core/form/components/FormSubLabelTooltip.vue';
import FormSubLabel from '/@/modules/core/form/components/FormSubLabel.vue';
import { FormLabelMixin } from '/@/modules/core/form/mixins/FormLabelMixin';
import FieldView from '@/modules/core/ui/components/wrappers/FieldView.vue';
import DatepickerView from '@/modules/core/ui/components/wrappers/DatepickerView.vue';

export default {
  compatConfig: {
    COMPONENT_V_MODEL: true
  },
  name: 'FormDate',
  emits: ['input'],
  slotsDefinition: {
    trigger: { props: Object },
    error: { validationProps: Object },
    subLabel: { hasErrors: Boolean }
  },
  mixins: [FormInputMixin, FormValidationMixin, HasCurrentEstateMixin, FormLabelMixin],
  components: {
    FloatLabel,
    ValidationProvider,
    FormSubLabelTooltip,
    FormSubLabel,
    FieldView,
    DatepickerView
  },
  props: {
    placeholder: {
      type: String,
      default: () => Lang.t('core.select-date'),
      required: false
    },
    subLabel: { type: String, default: null },
    hideSubLabelIcon: { type: Boolean, default: true },
    value: { type: [Date, String, Array] },
    icon: { type: String, default: 'cls-calendar' },
    minDate: { type: Date, required: false },
    maxDate: { type: Date, required: false, default: () => new Date() },
    isInline: { type: Boolean, default: false },
    range: { type: Boolean, default: false },
    position: { type: String },
    displayDateFormatter: { type: Function },
    format: { type: String, default: DateFormat.ISO },
    loading: { type: Boolean, default: false },
    yearsRange: { type: Array, default: () => [-100, 100] },
    isMobileNative: { type: Boolean, default: false }
  },
  computed: {
    computedValue: {
      get() {
        if (this.range) {
          return this.localComputedValue.map((val) => this.convertToDateFormat(val));
        }
        return this.convertToDateFormat(this.localComputedValue);
      },
      set(value) {
        let expectNewValue;
        if (this.range) {
          this.localComputedValue = value.map((val) => this.convertToDateFormat(val));
          expectNewValue = value.map((val) => this.convertToExpectedFormat(val));
        } else {
          this.localComputedValue = this.convertToDateFormat(value);
          expectNewValue = this.convertToExpectedFormat(value);
        }
        this.$emit('input', expectNewValue);
      }
    },
    locale() {
      return Lang.locale ?? Locale.ENGLISH_CANADA;
    }
  },
  methods: {
    convertToDateFormat(value) {
      if (isString(value)) {
        return new Date(this.convertToExpectedFormat(value));
      }
      return value ? new Date(value) : null;
    },
    convertToExpectedFormat(value) {
      if (value && this.format) {
        if (this.format === DateFormat.ISO) {
          return fromHyphenISOtoForwardSlashISO(dayjs(value, DateFormat.ISO).format(this.format));
        }
        return dayjs(value).format(this.format);
      }
      return value;
    },
    dateFormatter(value) {
      let callbackValue;
      if (this.range) {
        callbackValue = value.map((val) => this.convertToExpectedFormat(val));
      } else {
        callbackValue = this.convertToExpectedFormat(value);
      }
      return this.displayDateFormatter(callbackValue);
    }
  }
};
</script>

<style scoped>
:deep(.datepicker .select select) {
  padding-right: var(--ce-30px);
}

:deep(.datepicker .dropdown-menu) {
  z-index: var(--ce-z-index-20);
}
</style>
